/**
 * Bank Statements Service
 *
 * Multi-tenant aware service for bank statement OCR management.
 * Documents are stored in:
 * - GCS: documents/tenants/{tenantId}/bank-statements/{filename}
 * - Firestore: tenants/{tenantId}/bankStatements
 */

import {
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import {
  ref,
  uploadBytes
} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseService } from '../../../shared/services/FirebaseService';

class BankStatementsServiceClass {
  constructor() {
    this.db = null;
    this.storage = null;
    this.functions = null;
    this.tenantId = null;
    this.initialized = false;
  }

  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
  }

  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  async initialize() {
    if (this.initialized && this.db && this.storage) return;

    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      this.storage = FirebaseService.getStorage();
      this.functions = getFunctions(FirebaseService.getApp(), 'us-central1');
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize BankStatementsService:', error);
      throw error;
    }
  }

  getCollection() {
    const tenantId = this.getTenantId();
    return collection(this.db, `tenants/${tenantId}/bankStatements`);
  }

  async uploadStatement(file) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `documents/tenants/${tenantId}/bank-statements/${timestamp}_${sanitizedName}`;

      const storageRef = ref(this.storage, storagePath);

      await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          tenantId,
          documentType: 'bank-statement',
          originalName: file.name,
          uploadedBy: FirebaseService.getCurrentUser()?.email || 'unknown'
        }
      });

      return {
        success: true,
        storagePath,
        fileName: file.name
      };
    } catch (error) {
      console.error('Failed to upload bank statement:', error);
      throw new Error(`Failed to upload bank statement: ${error.message}`);
    }
  }

  subscribeToStatements(callback) {
    if (!this.db) {
      console.error('Firestore not initialized. Call initialize() first.');
      return () => {};
    }

    const colRef = this.getCollection();
    const q = query(colRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const statements = [];
      snapshot.forEach((doc) => {
        statements.push({ id: doc.id, ...doc.data() });
      });
      callback(statements);
    }, (error) => {
      console.error('Error listening to bank statements:', error);
    });

    return unsubscribe;
  }

  async deleteStatement(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const deleteFn = httpsCallable(this.functions, 'deleteBankStatement');
      const result = await deleteFn({ tenantId, docId });
      return result.data;
    } catch (error) {
      console.error('Failed to delete bank statement:', error);
      throw new Error(`Failed to delete bank statement: ${error.message}`);
    }
  }

  async reprocessStatement(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const reprocessFn = httpsCallable(this.functions, 'reprocessBankStatement');
      const result = await reprocessFn({ tenantId, docId });
      return result.data;
    } catch (error) {
      console.error('Failed to reprocess bank statement:', error);
      throw new Error(`Failed to reprocess bank statement: ${error.message}`);
    }
  }

  async getViewUrl(docId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const getUrlFn = httpsCallable(this.functions, 'getBankStatementViewUrl');
      const result = await getUrlFn({ tenantId, docId });
      return result.data.url;
    } catch (error) {
      console.error('Failed to get bank statement URL:', error);
      throw new Error(`Failed to get bank statement URL: ${error.message}`);
    }
  }
}

export const BankStatementsService = new BankStatementsServiceClass();
export { BankStatementsServiceClass };
